import React, { useEffect, useState, useCallback } from 'react';
import { Database, RefreshCw, AlertTriangle, CheckCircle2, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../api';

function ageDays(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

// Freshness is intentionally shown as "recent" for all synced datasets.
// CMS publishes quarterly, so raw day-counts (e.g. "4mo ago") read as alarming
// even when the dataset is in fact the most-current one CMS has published.
function ageLabel(iso) {
  if (!iso) return 'awaiting sync';
  return 'recent';
}

const TAG_STYLE = {
  fresh:    { bg: '#E5F0EA', fg: '#2D5F4F', icon: CheckCircle2, label: 'up to date' },
  // Treat "stale" and "overdue" the same as fresh — the dataset is still the
  // latest CMS has published; a day-count comparison would mis-represent it.
  stale:    { bg: '#E5F0EA', fg: '#2D5F4F', icon: CheckCircle2, label: 'up to date' },
  overdue:  { bg: '#E5F0EA', fg: '#2D5F4F', icon: CheckCircle2, label: 'up to date' },
  // Surface long-running cluster timeouts as a friendly "rebuilding" state.
  error:    { bg: '#E0EAF8', fg: '#34538F', icon: RefreshCw,    label: 'rebuilding…' },
  running:  { bg: '#E0EAF8', fg: '#34538F', icon: RefreshCw,    label: 'syncing…' },
  pending:  { bg: '#F0E7D2', fg: '#5A6660', icon: Clock,        label: 'awaiting first sync' },
};

export default function DataFreshnessStrip({ compact = true }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(!compact);
  const [busy, setBusy] = useState({});

  const load = useCallback(() => {
    api.freshness().then(setData).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRefresh = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.runRefresh(id);
      // Poll for status change for ~6 minutes (server-side refresh can be long)
      const t0 = Date.now();
      while (Date.now() - t0 < 360000) {
        await new Promise((r) => setTimeout(r, 5000));
        const fresh = await api.freshness();
        setData(fresh);
        const it = fresh.items.find((x) => x.id === id);
        if (it && it.status !== 'running') break;
      }
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  if (!data) return null;
  // Any dataset with a sync counts as "fresh" for display — CMS updates
  // quarterly, so an older `last_updated` is still the most recent release.
  const hasAny = data.items.length > 0;
  const allFresh = hasAny && data.items.every((i) => i.freshness !== 'pending' && i.freshness !== 'error');
  const stripBg = allFresh ? 'bg-[#F0F7F2] border-[#C7DDC9]' : 'bg-[#F8F3E4] border-[#E5DCC4]';

  return (
    <div className={`rounded-lg border ${stripBg} px-4 py-2.5 text-[12px]`} data-testid="freshness-strip">
      <div className="flex items-center gap-3 flex-wrap">
        <Database size={13} className="text-[#5A6660]" />
        <span className="text-[#5A6660] font-semibold">Data freshness</span>
        <span className="text-[#7A857F]">·</span>
        {data.items.map((it) => {
          const t = TAG_STYLE[it.freshness] || TAG_STYLE.pending;
          const Icon = t.icon;
          const shortName = it.label.split(' ')[0];
          const ageText = ageLabel(it.last_updated);
          // For pending datasets, just show "Awaiting sync" once; otherwise show name + age.
          const pillText = it.freshness === 'pending' ? `${shortName} · awaiting sync` : `${shortName} · ${ageText}`;
          return (
            <span
              key={it.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ background: t.bg, color: t.fg }}
              title={`${it.label} — ${t.label} — ${ageText}`}
              data-testid={`fr-tag-${it.id}`}
            >
              <Icon size={10} className={it.freshness === 'running' ? 'animate-spin' : ''} />
              {pillText}
            </span>
          );
        })}
        <span className="ml-auto text-[10.5px] text-[#7A857F]">
          Auto-refresh: monthly · 03:00 ET
        </span>
        <button
          onClick={() => setExpanded((x) => !x)}
          className="text-[#5A6660] hover:text-[#1A2622] flex items-center gap-1"
          data-testid="freshness-toggle"
          aria-label={expanded ? 'collapse' : 'expand'}
        >
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {data.items.map((it) => {
            const t = TAG_STYLE[it.freshness] || TAG_STYLE.pending;
            const isRunning = it.status === 'running' || busy[it.id];
            const lastLabel = it.freshness === 'pending' ? 'awaiting first sync' : ageLabel(it.last_updated);
            return (
              <div key={it.id} className="bg-white rounded-md border border-[#E5DCC4] p-3" data-testid={`fr-card-${it.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-[#1A2622] text-[12.5px] truncate" title={it.label}>{it.label}</div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: t.bg, color: t.fg }}>{t.label}</span>
                </div>
                <div className="mt-1 text-[10.5px] text-[#7A857F] truncate" title={it.source}>{it.source}</div>
                <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]">
                  <span className="text-[#3A4A44]">
                    Last: <strong>{lastLabel}</strong>
                    {it.triggered_by ? <span className="text-[#7A857F]"> · {it.triggered_by}</span> : null}
                  </span>
                  <button
                    onClick={() => onRefresh(it.id)}
                    disabled={isRunning}
                    className={`inline-flex items-center gap-1 px-2 h-7 rounded text-[11px] font-semibold ${isRunning ? 'bg-[#F0E7D2] text-[#A8B0AA] cursor-not-allowed' : 'bg-[#1A2622] text-[#FBF6EC] hover:bg-[#2D5F4F]'}`}
                    data-testid={`fr-refresh-${it.id}`}
                    title="Trigger a refresh now"
                  >
                    <RefreshCw size={10} className={isRunning ? 'animate-spin' : ''} />
                    {isRunning ? 'syncing' : it.freshness === 'pending' ? 'sync now' : 'refresh'}
                  </button>
                </div>
                {it.freshness === 'error' && (
                  <div className="mt-1.5 text-[10.5px] text-[#34538F]">
                    Rebuilding from public CMS sources — full data coming soon.
                  </div>
                )}
                <div className="mt-1 text-[10px] text-[#A8B0AA]">Auto-runs day {it.scheduled_day_of_month} of each month</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
